import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { pdf } from '@react-pdf/renderer';

import {
	Chip,
	Backdrop,
	Tooltip,
	Alert,
	Stack,
	Collapse,
	IconButton,
	CircularProgress,
	Box
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import Table from '../../../components/Table/Table';
import TableTitle from '../../../components/TableTitle/TableTitle';
import DecisionChip from '../../../components/core/DecisionChip/DecisionChip';
import ActionButtons from '../../../components/ActionButtons/ActionButtons';

import { formatTimestamp } from '../../../utils/dateUtils';
import { numberToMoney } from '../../../utils/numberUtils';
import {
	checkOverflow,
	createAndDownloadFile,
	getStatusColor,
	getChipTextColor,
	isDecisionChip,
	getEntityPrefix
} from '../../../utils/utilFunctions';
import { getLocalAuth } from '../../../utils/environmentUtils';
import { encryptQueryFilters } from '../../../utils/encryptionUtils';

import usePermissions from '../../../hooks/usePermissions';
import usePaginatedCall from '../../../hooks/usePaginatedCall';
import useIDToTitle from '../../../hooks/useIDToTitle';

import ReviewStageIcon from '../../../assets/customIcons/reviewStageIcon';

import {
	useGetProgramApplicationsQuery,
	useLazyGetProgramApplicationsQuery,
	useGetApplicantEmailsQuery,
	useLazyGetProgramApplicationsWithoutPaginationQuery
} from '../../../services/endpoints/programsEndpoints';
import {
	useLazyExportReviewReportQuery,
	useLazyExportSubmissionReportQuery
} from '../../../services/endpoints/exportReportEndpoints';

import IndividualApplication from '../../IndividualApplication/IndividualApplication';
import ApplicationsTemplate from './ApplicationsTemplate';
import BasicCellText from './BasicCellText';

const ManageApplication = ({ setJustDeletedApplication }) => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	let { programID, appID } = useParams();
	programID = programID?.replace('p-', '');
	const [finalData, setFinalData] = useState([]);
	const [noApplications, setNoApplications] = useState(false);

	const filterBaseName = 'PROGRAM_APPLICATIONS';
	const allApplications = usePaginatedCall(useGetProgramApplicationsQuery, {
		filterBaseName,
		iFrame: searchParams.get('iFrame')
	});

	const accountSource = useSelector((state) => state?.login?.accountSource);
	const filters = useSelector((state) => state.globalProgramVariables?.filters);

	const apiToken = getLocalAuth(accountSource)
		?.access_token.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;');

	const [{ isLoading: csvDataIsLoading, isFetching: csvDataIsFetching }] =
		useLazyGetProgramApplicationsQuery();

	const [getProgramApplicationsWithoutPagination] =
		useLazyGetProgramApplicationsWithoutPaginationQuery();

	const [getReviewResponse] = useLazyExportReviewReportQuery();

	const [getSubmissionResponse] = useLazyExportSubmissionReportQuery();

	const tableExportAvailable = allApplications?.rowCount > 0;

	useEffect(() => {
		if (allApplications.pageSize > 0 && allApplications?.rows?.results) {
			const copiedRowData = allApplications?.rows?.results?.map((e, index) => {
				const stagePrefix = getEntityPrefix({
					type:
						e?.form_stage_id?.stage_type === 'application'
							? 'submission-stage'
							: 'evaluation-stage',
					isInstance: true
				});

				return {
					programId: e.programId,
					project_title: e.project_title,
					org_name: e.org_name,
					unique_identifier: `api-${e.unique_identifier}`,
					status: e.status,
					can_revert_decision: e.can_revert_decision,
					id: index + 1,
					budgetWorksheet: e.budgetWorksheet,
					stage_name: e.form_stage_id?.stage_name,
					stage_unique_identifier:
						e.form_stage_id && `${stagePrefix}${e.form_stage_id?.unique_identifier}`,
					stage_status: e.form_stage_id?.status,
					stage_type: e.form_stage_id?.stage_type,
					submitter: e.submitter,
					submission_date: e.first_submission_stage_submitted_on,
					last_updated_on: e.forms_last_updated_on
				};
			});

			setFinalData(copiedRowData);
			setNoApplications(
				allApplications.rows &&
					copiedRowData.length === 0 &&
					allApplications.filterModel.length === 0
			);
		}
	}, [allApplications?.rows?.results]);

	const [openTooltip, setOpenTooltip] = useState(0);
	const [isLoading, setLoading] = useState(false);
	const [alertBalloon, setAlertBalloon] = useState(false);
	const { hasEditPermissions } = usePermissions();
	const handleAlert = (message, status, timeout = true) => {
		setAlertBalloon({ isOpen: true, message, status });
		timeout &&
			setTimeout(() => {
				setAlertBalloon(false);
			}, 2000);
	};

	useEffect(() => {
		if (hasEditPermissions) {
			setLoading(true);
			setTimeout(() => setLoading(false), 200);
		}
	}, [hasEditPermissions]);

	const columns = [
		{
			field: 'project_title',
			headerName: 'Project Title',
			description: 'Project Title',
			renderCell: (cellValues) => {
				const { status, unique_identifier: displayId } = cellValues.row ?? {};
				const title = cellValues.formattedValue;
				const programId = cellValues.row.programId;
				const titleData = {
					displayId,
					title,
					status,
					entityType: 'Application Instance'
				};

				const handleCardClick = () => {
					navigate(
						`/programs/p-${programId}/applications/${displayId}?${searchParams.toString()}&formtype=application${
							searchParams?.get('iFrame') === 'true' ? '&iFrame=true' : ''
						}`
					);
				};

				return <TableTitle titleData={titleData} handleCardClick={handleCardClick} />;
			},
			flex: 1,
			minWidth: 400
		},
		{
			field: 'org_name',
			headerName: 'Organization',
			description: 'Organization',
			renderCell: (cellValues) => (
				<BasicCellText
					isLink={true}
					cellValues={cellValues}
					openTooltip={openTooltip}
					setOpenTooltip={setOpenTooltip}
				/>
			),
			flex: 1,
			minWidth: 100
		},
		{
			field: 'submitter',
			headerName: 'Submitter',
			description: 'Submitter',
			renderCell: (cellValues) => (
				<BasicCellText
					isLink={true}
					cellValues={cellValues}
					openTooltip={openTooltip}
					setOpenTooltip={setOpenTooltip}
				/>
			),
			flex: 1,
			minWidth: 100
		},
		{
			field: 'status',
			headerName: 'Application Status',
			renderCell: (cellValues) => (
				<Tooltip
					open={openTooltip === `status-${cellValues?.id}`}
					onOpen={(e) => checkOverflow(e) && setOpenTooltip(`status-${cellValues?.id}`)}
					onClose={() => setOpenTooltip(0)}
					arrow
					placement="top"
					title={cellValues?.formattedValue}
				>
					{isDecisionChip(cellValues?.formattedValue) ? (
						<DecisionChip label={cellValues?.formattedValue} />
					) : (
						<Chip
							data-testid={`${cellValues?.field}-${cellValues?.id}`}
							label={cellValues?.formattedValue}
							sx={{
								backgroundColor: getStatusColor(cellValues?.formattedValue),
								color: getChipTextColor(cellValues?.formattedValue)
							}}
							size="small"
						/>
					)}
				</Tooltip>
			),
			flex: 1,
			headerAlign: 'right',
			align: 'right',
			minWidth: 150
		},
		{
			field: 'stage_name',
			headerName: 'Application Stage',
			description: 'Application Stage',
			renderCell: (cellValues) => {
				if (
					cellValues.formattedValue &&
					cellValues.row.status &&
					cellValues.row.unique_identifier &&
					!isDecisionChip(cellValues.row.status)
				) {
					cellValues.formattedValue = `${cellValues.formattedValue} [${cellValues.row.stage_unique_identifier}]`;

					return (
						<BasicCellText
							cellValues={cellValues}
							StartIcon={ReviewStageIcon}
							IconStatus={cellValues.row.stage_status}
						/>
					);
				}
			},
			flex: 1,
			minWidth: 100
		},
		{
			field: 'submission_date',
			headerName: 'Submission Date',
			description: 'Submission Date',
			renderCell: (cellValues) => (
				<BasicCellText
					isDate={cellValues?.formattedValue !== 'N/A' && cellValues?.formattedValue !== ''}
					cellValues={cellValues}
					openTooltip={openTooltip}
					setOpenTooltip={setOpenTooltip}
				/>
			),
			flex: 1,
			minWidth: 100
		},
		{
			field: 'last_updated_on',
			headerName: 'Recent Activity Date',
			description: 'Recent Activity Date',
			renderCell: (cellValues) => {
				if (cellValues.row.status !== 'Not Started') {
					return (
						<BasicCellText
							isDate={true}
							cellValues={cellValues}
							openTooltip={openTooltip}
							setOpenTooltip={setOpenTooltip}
						/>
					);
				} else {
					return <></>;
				}
			},
			flex: 1,
			minWidth: 100
		},
		{
			field: 'budgetWorksheet',
			type: 'number',
			headerName: 'Budget',
			description: 'Budget',
			renderCell: (cellValues) => {
				cellValues.formattedValue = numberToMoney(
					cellValues?.formattedValue?.totalProposedCost || 0
				);

				return (
					<BasicCellText
						isLink={true}
						cellValues={cellValues}
						openTooltip={openTooltip}
						setOpenTooltip={setOpenTooltip}
					/>
				);
			},
			minWidth: 50
		}
	];

	hasEditPermissions &&
		columns.push({
			field: 'action',
			headerName: 'Actions',
			description: 'Actions',
			renderCell: (cellValues) => {
				return (
					<div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
						<ActionButtons
							item={cellValues?.row}
							application_id={cellValues?.row?.unique_identifier?.replace('api-', '')}
							type={'application instance'}
							status={cellValues?.row?.status?.toLowerCase()}
							handleAlert={handleAlert}
						/>
					</div>
				);
			},
			flex: 2,
			minWidth: 450
		});

	const [orderedColumns, setOrderedColumns] = useState(columns);

	const updateOrderedColumns = (updatedOrderedColumns) => setOrderedColumns(updatedOrderedColumns);

	const exportTableToCSV = async () => {
		let csv = '';

		csv += orderedColumns.map((column) => column.headerName).join(',') + '\n';

		try {
			const response = await getProgramApplicationsWithoutPagination({
				programID,
				apiToken,
				finalQuery: encryptQueryFilters({ filters: filters?.[filterBaseName]?.filterModel ?? [] })
			});

			if ('error' in response) {
				throw new Error('Error getting applications data:', response.error);
			}

			response.data.forEach((row) => {
				const rowData = [];

				orderedColumns.forEach((column) => {
					switch (column.field) {
						case 'project_title':
							rowData.push(row.project_title || 'n/a');
							break;
						case 'budgetWorksheet':
							rowData.push(row.budgetWorksheet?.totalProposedCost ?? 0);
							break;
						case 'submission_date': {
							const submittedOn = row.first_submission_stage_submitted_on;

							rowData.push(
								submittedOn && !isNaN(Date.parse(submittedOn))
									? formatTimestamp(submittedOn)
									: submittedOn
							);
							break;
						}
						case 'last_updated_on':
							if (row.status !== 'Not Started') {
								const updatedOn = row.forms_last_updated_on;

								rowData.push(
									updatedOn && !isNaN(Date.parse(updatedOn))
										? formatTimestamp(updatedOn)
										: updatedOn
								);
							} else {
								rowData.push('');
							}
							break;
						case 'stage_name':
							rowData.push(row.form_stage_id?.name);
							break;
						case 'form_stage_id.start_date':
							if (row?.status === 'Rec. Award') {
								rowData.push('');
							} else {
								rowData.push(
									formatTimestamp(
										row?.form_stage_id?.start_date != null
											? formatTimestamp(row?.form_stage_id?.start_date)
											: formatTimestamp(row?.process_start_date)
									)
								);
							}
							break;
						case 'form_stage_id.end_date':
							if (row?.status === 'Rec. Award') {
								rowData.push('');
							} else {
								rowData.push(
									formatTimestamp(
										row?.form_stage_id?.end_date != null
											? formatTimestamp(row?.form_stage_id?.end_date)
											: formatTimestamp(row?.process_end_date)
									)
								);
							}
							break;
						default:
							rowData.push(row[column.field]);
							break;
					}
				});

				csv += rowData.join(',') + '\n';
			});

			const blob = new Blob([csv], { type: 'text/csv' });
			const fileName = `p-${programID}-applications-${new Date().toLocaleString()}.csv`;

			createAndDownloadFile(blob, fileName);
		} catch (error) {
			console.error(error);
		}
	};

	const programTitle = useIDToTitle('program');

	const exportTableToPDF = async () => {
		const activeFilters = allApplications?.filterModel.map((filter) => {
			const targetColumn = columns.find((column) => column.field === filter.columnField);

			return {
				columnName: targetColumn.headerName,
				operator: filter.operatorValue,
				filterValue: filter.value
			};
		});

		const tableData = [];

		try {
			const response = await getProgramApplicationsWithoutPagination({
				programID,
				apiToken,
				finalQuery: encryptQueryFilters({ filters: filters?.[filterBaseName]?.filterModel ?? [] })
			});

			if ('error' in response) {
				throw new Error('Error getting applications data:', response.error);
			}

			response.data.forEach((row) => {
				const submittedOn = row.first_submission_stage_submitted_on;

				const formattedRowData = {
					projectTitle: row.project_title || 'n/a',
					orgName: row.org_name,
					submitter: row.submitter,
					status: row.status,
					activeStage: row.form_stage_id?.name,
					submittedOn:
						submittedOn && !isNaN(Date.parse(submittedOn))
							? formatTimestamp(submittedOn)
							: submittedOn,
					budget: row.budgetWorksheet?.totalProposedCost ?? 0
				};

				if (row.status !== 'Not Started') {
					const updatedOn = row.forms_last_updated_on;
					formattedRowData.lastUpdatedOn =
						updatedOn && !isNaN(Date.parse(updatedOn)) ? formatTimestamp(updatedOn) : updatedOn;
				} else {
					formattedRowData.lastUpdatedOn = '';
				}

				tableData.push(formattedRowData);
			});

			const blob = await pdf(
				<ApplicationsTemplate
					programTitle={programTitle}
					activeFilters={activeFilters}
					tableData={tableData}
				/>
			).toBlob();
			const fileName = `p-${programID}-applications-${new Date().toLocaleString()}.pdf`;

			createAndDownloadFile(blob, fileName);
		} catch (error) {
			console.error(error);
		}
	};

	const exportReviewResponse = async () => {
		try {
			const response = await getReviewResponse({
				programID,
				apiToken
			});
			if (response) {
				const { blob: data, contentDisposition } = response.data;
				let fileName = 'download.zip';
				if (contentDisposition) {
					const match = contentDisposition.match(/filename="(.+)"/);
					if (match && match[1]) {
						fileName = match[1];
					}
				}
				const blob = new Blob([data], { type: 'application/zip' });
				createAndDownloadFile(blob, fileName);
			}
		} catch (error) {
			console.error(error);
		}
	};
	const exportSubmissionResponse = async () => {
		try {
			const response = await getSubmissionResponse({
				programID,
				apiToken
			});
			if (response) {
				const { blob: data, contentDisposition } = response.data;
				let fileName = 'download.zip';
				if (contentDisposition) {
					const match = contentDisposition.match(/filename="(.+)"/);
					if (match && match[1]) {
						fileName = match[1];
					}
				}
				const blob = new Blob([data], { type: 'application/zip' });
				createAndDownloadFile(blob, fileName);
			}
		} catch (error) {
			console.error(error);
		}
	};
	return (
		<>
			{noApplications ? (
				<Stack sx={{ width: '100%', marginTop: '20px' }} spacing={2}>
					<Alert variant="outlined" severity="info">
						No applications have been received for this program.
						<br />
						Once an application has been submitted, it will appear on this page.
					</Alert>
				</Stack>
			) : isLoading ? (
				<Box sx={{ display: 'flex', margin: '50px 0px', justifyContent: 'center' }}>
					<CircularProgress data-testid={'application-process-loading'} />
				</Box>
			) : appID ? (
				<IndividualApplication
					appId={appID}
					handleApplicationListAlert={handleAlert}
					setJustDeletedApplication={setJustDeletedApplication}
				/>
			) : (
				<>
					<Collapse in={alertBalloon}>
						<Alert
							severity={alertBalloon?.status}
							variant="outlined"
							action={
								<IconButton
									aria-label="close"
									color={alertBalloon?.status}
									size="small"
									onClick={() => {
										setAlertBalloon(false);
									}}
								>
									<CloseIcon fontSize="inherit" />
								</IconButton>
							}
							sx={{ mb: 2, mt: 2 }}
						>
							{alertBalloon?.message}
						</Alert>
					</Collapse>
					<Backdrop
						sx={{ zIndex: (theme) => theme.zIndex.drawer + 5 }}
						open={csvDataIsLoading || csvDataIsFetching}
					>
						<CircularProgress />
					</Backdrop>
					<Table
						columns={columns}
						rowHeight={true}
						enableAutoPageSize
						rows={finalData || []}
						rowCount={allApplications?.rowCount}
						pagination={allApplications?.pagination}
						pageSize={
							allApplications?.pageSize > 0
								? allApplications?.pageSize
								: allApplications?.results?.length
						}
						paginationMode={allApplications?.paginationMode}
						onPageChange={(e) => allApplications?.onPageChange(e)}
						onPageSizeChange={(e) => allApplications?.onPageSizeChange(e)}
						page={allApplications?.page}
						loading={allApplications?.loading}
						sortingMode={allApplications?.sortingMode}
						onSortModelChange={(e) => allApplications?.onSortModelChange(e)}
						onFilterModelChange={(e) => allApplications?.onFilterModelChange(e)}
						filterModel={allApplications?.filterModel}
						sortModel={allApplications?.sortModel}
						emailType={'Application'}
						updateOrderedColumns={updateOrderedColumns}
						tableExportAvailable={tableExportAvailable}
						exportTableToCSV={exportTableToCSV}
						exportTableToPDF={exportTableToPDF}
						emailEndpoint={useGetApplicantEmailsQuery}
						exportReviewResponse={exportReviewResponse}
						exportSubmissionResponse={exportSubmissionResponse}
					/>
				</>
			)}
		</>
	);
};

export default ManageApplication;
